<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import Checkbox from 'primevue/checkbox'
import { userUpdateSchema, type UserUpdateValues } from './user-schema'
import UserRightsMatrix from './UserRightsMatrix.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import BInput from '@lib/components/base/BInput.vue'
import BDateTimeInput from '@lib/components/base/BDateTimeInput.vue'
import { sdk } from '@/graphql/client'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { useAuthStore } from '@/stores/auth'
import { useFormatters } from '@lib/composables/useFormatters'
import type { User } from '@/composables/useUsersData'

const props = defineProps<{ user: User }>()
const emit = defineEmits<{ saved: []; deleted: []; cancel: [] }>()

const { t } = useI18n()
const toast = useToast()
const { confirm } = useConfirm()
const auth = useAuthStore()
const fmt = useFormatters()

function initialValues(): UserUpdateValues {
    return {
        firstName: props.user.firstName ?? '',
        lastName: props.user.lastName ?? '',
        email: props.user.email ?? '',
        validUntil: props.user.profile?.validUntil ?? null,
        isActive: Boolean(props.user.isActive),
        isSuperuser: Boolean(props.user.isSuperuser)
    }
}

const { handleSubmit, defineField, errors, resetForm, meta, values, setFieldValue } =
    useForm<UserUpdateValues>({
        validationSchema: toTypedSchema(userUpdateSchema),
        initialValues: initialValues()
    })

const [firstName] = defineField('firstName')
const [lastName] = defineField('lastName')
const [email] = defineField('email')
const [validUntil] = defineField('validUntil')
const isActive = computed({
    get: () => Boolean(values.isActive),
    set: (v: boolean) => setFieldValue('isActive', v)
})
const isSuperuser = computed({
    get: () => Boolean(values.isSuperuser),
    set: (v: boolean) => setFieldValue('isSuperuser', v)
})

const submitting = ref(false)
const deleting = ref(false)
const matrixRef = ref<{ isDirty: () => boolean } | null>(null)

const canDelete = computed(() => auth.user?.isSuperuser === true)

const onSubmit = handleSubmit(async vals => {
    submitting.value = true
    try {
        const r = await sdk.AdminUpdateUser({
            id: props.user.id,
            firstName: vals.firstName,
            lastName: vals.lastName,
            email: vals.email,
            isActive: vals.isActive,
            isSuperuser: vals.isSuperuser,
            validUntil: vals.validUntil ?? ''
        })
        if (r.admin?.user?.update?.ok) {
            toast.success(t('users.toast.profileSaved'))
            resetForm({ values: vals })
            emit('saved')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        submitting.value = false
    }
})

async function onDelete() {
    const displayName =
        `${props.user.firstName} ${props.user.lastName}`.trim() ||
        props.user.username ||
        props.user.email
    const ok = await confirm({
        title: t('users.confirm.deleteTitle'),
        message: t('users.confirm.deleteBody', { name: displayName }),
        severity: 'danger'
    })
    if (!ok) return
    deleting.value = true
    try {
        const r = await sdk.AdminDeleteUser({ id: props.user.id })
        if (r.admin?.user?.delete?.ok) {
            toast.success(t('users.toast.deleted'))
            emit('deleted')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        deleting.value = false
    }
}

function panelIsDirty(): boolean {
    return meta.value.dirty || matrixRef.value?.isDirty() === true
}

async function onCancel() {
    if (panelIsDirty()) {
        const ok = await confirm({
            title: t('users.confirm.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    emit('cancel')
}

watch(
    () => props.user.id,
    () => resetForm({ values: initialValues() })
)

defineExpose({ isDirty: panelIsDirty })
</script>

<template>
    <form class="space-y-6" @submit.prevent="onSubmit">
        <!-- Sekce 1: EDITOVAT -->
        <section>
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('users.form.section.edit') }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label class="block">
                    <span class="text-xs font-semibold text-gray-600 mb-1 block">{{ t('users.form.firstName') }}</span>
                    <BInput v-model="firstName" :invalid="!!errors.firstName" />
                    <span v-if="errors.firstName" class="text-xs text-red mt-1 block">{{ errors.firstName }}</span>
                </label>
                <label class="block">
                    <span class="text-xs font-semibold text-gray-600 mb-1 block">{{ t('users.form.lastName') }}</span>
                    <BInput v-model="lastName" :invalid="!!errors.lastName" />
                    <span v-if="errors.lastName" class="text-xs text-red mt-1 block">{{ errors.lastName }}</span>
                </label>
            </div>
            <label class="block mt-3">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">
                    {{ t('users.form.email') }} *
                </span>
                <BInput v-model="email" :invalid="!!errors.email" />
                <span v-if="errors.email" class="text-xs text-red mt-1 block">{{ errors.email }}</span>
            </label>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 items-end">
                <label class="block">
                    <span class="text-xs font-semibold text-gray-600 mb-1 block">{{ t('users.form.validUntil') }}</span>
                    <BDateTimeInput v-model="validUntil" :show-time="false" />
                </label>
                <div class="flex flex-col gap-2">
                    <label class="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                            :binary="true"
                            :model-value="isActive"
                            @update:model-value="(v: boolean) => (isActive = v)"
                        />
                        <span class="text-xs text-gray-700">{{ t('users.form.isActive') }}</span>
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                            :binary="true"
                            :model-value="isSuperuser"
                            @update:model-value="(v: boolean) => (isSuperuser = v)"
                        />
                        <span class="text-xs text-gray-700">{{ t('users.form.isSuperuser') }}</span>
                    </label>
                </div>
            </div>
        </section>

        <!-- Sekce 2: PRÁVA -->
        <section class="border-t border-gray-200 pt-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('users.form.section.rights') }}
            </h3>
            <UserRightsMatrix ref="matrixRef" :user="props.user" />
        </section>

        <!-- Sekce 3: METADATA -->
        <section class="border-t border-gray-200 pt-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('users.form.section.metadata') }}
            </h3>
            <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('users.metadata.username') }}</dt>
                    <dd class="text-ink font-mono">{{ props.user.username }}</dd>
                </div>
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('users.metadata.dateJoined') }}</dt>
                    <dd class="text-ink tabular-nums">{{ fmt.dateTime(props.user.dateJoined) }}</dd>
                </div>
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('users.metadata.lastLogin') }}</dt>
                    <dd class="text-ink tabular-nums">
                        <span v-if="props.user.lastLogin">{{ fmt.dateTime(props.user.lastLogin) }}</span>
                        <span v-else class="text-gray-400">{{ t('users.metadata.lastLoginNever') }}</span>
                    </dd>
                </div>
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('users.metadata.isStaff') }}</dt>
                    <dd class="text-ink">
                        {{ props.user.isStaff ? t('users.metadata.yes') : t('users.metadata.no') }}
                    </dd>
                </div>
            </dl>
        </section>

        <!-- Footer -->
        <div class="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
            <div>
                <BBtn
                    severity="danger"
                    size="small"
                    :disabled="!canDelete || deleting"
                    :title="canDelete ? '' : t('users.confirm.deletePermissionTooltip')"
                    type="button"
                    @click="onDelete"
                >
                    {{ t('common.actions.delete') }}
                </BBtn>
            </div>
            <div class="flex gap-2">
                <BBtn severity="secondary" size="small" type="button" @click="onCancel">
                    {{ t('common.actions.cancel') }}
                </BBtn>
                <BBtn size="small" type="submit" :disabled="!meta.valid || !meta.dirty || submitting">
                    {{ t('common.actions.save') }}
                </BBtn>
            </div>
        </div>
    </form>
</template>
